import { api } from "../api.js";
import { t, translatePage, getLocale } from "../i18n.js";
import { navigate } from "../router.js";
import { icon } from "../icon.js";
import { renderFilterMenu } from "../components/filter-menu.js";
import { renderMenu } from "../components/menu.js";
import "../components/location-card.js";
import { renderLoading } from "../components/loading.js";
import { canLocate, distanceKm, getCurrentPosition, locateErrorKey } from "../geolocation.js";
import { canEdit } from "../trip-role.js";
import { hasCapability } from "../session.js";
import { formatDateRange } from "../format.js";
import { CATEGORIES } from "../categories.js";

// Radii for the "near me" filter, in km. Coarse on purpose: the question is
// "walkable / a short drive / same region", not a precise number.
const DISTANCE_RADII_KM = [1, 2, 5, 10, 25];
const ANY_DISTANCE = "any";

// The orders the list can be read in. "added" rather than the trips list's
// "newest": the API returns locations oldest-first, in creation order, and
// calling that "newest" would be a lie about the list.
//
// It really is creation order now. Until migration 0012 the list was ordered
// by an items.sort_order column that no UI ever set and three writers disagreed
// about, so a location added here sorted ahead of anything the assistant had
// added -- "as added" put a new location in the middle of the list. The column
// is gone and the order is created_at.
//
// A plain renderMenu, not a group in the filter menu beside it. Sorting is not
// filtering - it changes the order of the answer, not which questions the list
// is answering - and trips-page.js already established the separate sort
// trigger with this icon. Consistency between the app two list screens is
// worth more than one fewer control, and collapsing the filters into one
// trigger is exactly what made room for this one.
//
// Every order can be reversed as of Stage 43, by tapping it again while it is
// the selection (menu.js's `reversible`). Each one starts in the direction
// listed in sorted() below; only the primary key flips.
const SORTS = ["added", "title", "date"];
const DEFAULT_SORT = "added";

const ANY_TAG = "any";
const ANY_DATE = "any";
// "Not scheduled" is the reason the date filter is a small preset list and not
// only a range picker: while a trip is being planned, "what have I not placed
// yet" is the question asked most, and no range can express it.
const UNSCHEDULED = "unscheduled";
const SCHEDULED = "scheduled";

// The toolbar is one non-wrapping row - search (flexible) + filter menu +
// "New location" - which is what makes it fit 324px, where the previous
// row of four category pills did not (they only scrolled; see stage-04.md
// Section 5 and stage-06.md Section 1). Its .list-toolbar/.list-search rules
// are shared with the trips list as of Stage 15 Milestone 2.
//
// Consequence of putting a search box next to the filter: filtering moved
// from the server to the client. The trip's locations are fetched once and
// every filter is applied in memory, so typing gives instant feedback and
// switching filters no longer refetches. The backend's ?category= filter still
// exists, just unused here. This is fine at realistic per-trip location counts.
// (This comment used to say a `q` predicate and pagination were a todo.md item.
// They are not: the 2026-08-29 backlog review dropped that entry deliberately,
// and todo.md forbids reconstructing one without asking.)
//
// As of Stage 26 Milestone 4 the filters live behind one trigger rather than
// one button each - see components/filter-menu.js for why that shape.
// Takes the whole trip rather than just its id since Stage 14 Milestone 4: the
// toolbar has to know whether the reader may add a location, and `trip.role` is
// where that lives.
export async function renderLocationsTab(container, trip, { signal } = {}) {
  const tripId = trip.id;
  const editable = canEdit(trip);
  // What the toolbar was set to when this history entry was last left. See
  // saveToolbarState below for why it is read here rather than reset.
  const saved = window.history.state?.locationsToolbar ?? {};
  let activeFilter = saved.category ?? "all";
  let query = saved.query ?? "";
  let allLocations = [];
  let radiusKm = null;
  let sort = saved.sort ?? DEFAULT_SORT;
  let reversed = saved.reversed ?? false;
  let activeTag = saved.tag ?? ANY_TAG;
  // The date filter has more states than a value: three presets plus a range,
  // so it carries a small object rather than a string. `mode` is what the
  // predicate switches on; from/to are only meaningful in "range".
  let dateFilter = saved.date ?? { mode: ANY_DATE, from: "", to: "" };
  // Kept between selections so switching 5km -> 10km does not ask the device
  // again. Not fetched on load: asking for someone's position before they
  // have expressed any interest in it is rude, and the permission prompt
  // would arrive unexplained.
  // The position the filter is measuring from. Held here because applyFilters
  // needs it synchronously on every keystroke and re-render; it is refreshed
  // rather than kept forever (see the distance group below).
  let devicePosition = null;

  container.innerHTML = `
    <div class="locations-tab">
      <div class="list-toolbar">
        <div class="list-search">
          ${icon("search", { className: "list-search__icon" })}
          <input type="search" name="q" autocomplete="off" data-i18n-placeholder="locations.searchPlaceholder" data-i18n-aria-label="locations.searchPlaceholder" />
        </div>
        <div class="locations-filter-slot"></div>
        <div class="locations-sort-slot"></div>
        ${editable ? `<div class="locations-new-slot"></div>` : ""}
      </div>
      <p class="locations-distance-status" role="status" hidden></p>
      <p class="locations-distance-note" hidden></p>
      <p class="locations-empty" data-i18n="locations.empty" hidden></p>
      <p class="locations-empty locations-empty--no-matches" data-i18n="locations.noMatches" hidden></p>
      <div class="location-list"></div>
    </div>
  `;
  translatePage(container);

  const list = container.querySelector(".location-list");
  const emptyState = container.querySelector(".locations-empty:not(.locations-empty--no-matches)");
  const noMatchesState = container.querySelector(".locations-empty--no-matches");

  function hasCoordinates(location) {
    return typeof location.lat === "number" && typeof location.lng === "number";
  }

  function distanceActive() {
    return Boolean(radiusKm && devicePosition);
  }

  // ignoreDistance is for counting what the radius left out: everything the
  // other filters keep, regardless of where it is.
  function matches(location, { ignoreDistance = false } = {}) {
    if (activeFilter !== "all" && location.category !== activeFilter) return false;
    // A location with no coordinates cannot be within a radius, so a radius
    // hides it. Keeping them used to be the rule (Stage 13), but when nothing
    // measurable was in range the list was only unplaced locations and read as
    // "the filter did nothing". The note below says how many were left out,
    // so the gap in the data is still visible.
    if (!ignoreDistance && distanceActive()) {
      if (!hasCoordinates(location)) return false;
      if (distanceKm(devicePosition, { lat: location.lat, lng: location.lng }) > radiusKm) return false;
    }
    if (activeTag !== ANY_TAG && !(location.tags ?? []).includes(activeTag)) return false;
    if (!matchesDate(location)) return false;
    if (!query) return true;
    // Tags are searched as well as the title: they are words somebody chose
    // for this location, so a search that ignored them would miss the most
    // deliberate labels on the page.
    return `${location.title} ${(location.tags ?? []).join(" ")}`.toLowerCase().includes(query);
  }

  function matchesDate(location) {
    const dates = location.dates ?? [];
    switch (dateFilter.mode) {
      case UNSCHEDULED:
        return dates.length === 0;
      case SCHEDULED:
        return dates.length > 0;
      case "range": {
        const { from, to } = dateFilter;
        if (!from && !to) return true;
        // Overlap, not containment: a hotel booked the 5th to the 12th is
        // part of what happens on the 6th, so asking for the 6th has to find
        // it. An open end means unbounded in that direction, so a range with
        // only a start reads as "from here on".
        return dates.some((d) => (!to || d.start_date <= to) && (!from || d.end_date >= from));
      }
      default:
        return true;
    }
  }

  // Every tag in use on this trip, for the filter's options. Derived from the
  // list already in memory rather than from GET /trips/{id}/tags: the tab holds
  // every location, so asking the server for a projection of what it is already
  // looking at would be a second request for nothing. (The editor does call
  // that endpoint, because it holds one location and would otherwise fetch the
  // whole trip to learn three words.)
  function tripTags() {
    const seen = new Map();
    for (const location of allLocations) {
      for (const tag of location.tags ?? []) {
        if (!seen.has(tag)) seen.set(tag, tag);
      }
    }
    return [...seen.values()].sort((a, b) => {
      const x = a.toLowerCase();
      const y = b.toLowerCase();
      if (x !== y) return x < y ? -1 : 1;
      return a < b ? -1 : a > b ? 1 : 0;
    });
  }

  // Sorts a copy rather than in place. allLocations is in the order the API
  // returned, which is exactly what "as added" means, so it must never be
  // reordered.
  //
  // Being precise about what protects that, because it is easy to
  // misattribute: the caller already hands this a fresh array from .filter(),
  // so today the spread is belt and braces rather than the thing doing the
  // work -- removing it does not break anything, and no test catches it. It
  // stays so that sorted() is safe to call on any array, including allLocations
  // itself if a later caller skips the filter. Same shape as trips-page.js.
  function sorted(locations) {
    const out = [...locations];
    // -1 while the reader has flipped the order. It multiplies the primary
    // comparison only: whatever a rule puts last (undated locations) stays
    // last in both directions.
    const dir = reversed ? -1 : 1;
    if (sort === "added") {
      // Oldest first is the fetch order itself, so newest first is simply
      // that order backwards.
      if (reversed) out.reverse();
    } else if (sort === "title") {
      // Under the active locale, so German umlauts sort where a German reader
      // expects rather than after z. numeric so "Hut 2" precedes "Hut 10".
      const collator = new Intl.Collator(getLocale(), { sensitivity: "base", numeric: true });
      out.sort((a, b) => dir * collator.compare(a.title, b.title));
    } else if (sort === "date") {
      // Earliest first, by the first range - the ranges arrive already sorted
      // from collapseDateRanges, so dates[0] is the earliest without sorting
      // them again. ISO dates compare as strings.
      //
      // A location with no dates goes last rather than first: it is
      // unscheduled, not imminent. That is the rule the trips list uses for a
      // trip with no start date, and it matters more here, since on a
      // half-planned trip most locations have no days yet and they would
      // otherwise bury the ones that do.
      const startOf = (location) => location.dates?.[0]?.start_date ?? null;
      out.sort((a, b) => {
        const x = startOf(a);
        const y = startOf(b);
        if (!x && !y) return 0;
        if (!x) return 1;
        if (!y) return -1;
        return dir * (x < y ? -1 : x > y ? 1 : 0);
      });
    }
    return out;
  }

  // Whether this history entry is already the one holding a filtered list.
  //
  // The rule, and the reason for the flag: leaving the neutral toolbar pushes
  // ONE entry, and every change after that overwrites it. So overview ->
  // filter -> location -> Back returns to the filtered list, and one more Back
  // reaches the unfiltered one - however many times the filter was changed in
  // between. Without the flag, a list of every filter anybody tried would sit
  // between the reader and the way out.
  //
  // Same mechanism as the map tab's camera one tab over (trip-detail-page.js),
  // and the same tradeoff: state on the entry, not in the URL. Nothing here is
  // shareable, and a link to a filtered list is not what a Back press is for.
  let toolbarEntryPushed = Boolean(window.history.state?.locationsToolbar);
  // Not until the first render is on screen: restoring saved state must not
  // itself push an entry.
  let toolbarSaveArmed = false;

  function toolbarState() {
    // Distance is deliberately absent. It is the one filter that is not a
    // value but a question put to the device, and re-applying it on a Back
    // press would either fire a permission prompt nobody asked for or restore
    // a radius measured from a position we no longer hold.
    const state = {};
    if (activeFilter !== "all") state.category = activeFilter;
    if (query) state.query = query;
    if (sort !== DEFAULT_SORT) state.sort = sort;
    if (reversed) state.reversed = true;
    if (activeTag !== ANY_TAG) state.tag = activeTag;
    if (dateFilter.mode !== ANY_DATE) state.date = dateFilter;
    return state;
  }

  function saveToolbarState() {
    if (!toolbarSaveArmed) return;
    const state = toolbarState();
    const neutral = Object.keys(state).length === 0;
    const base = { ...window.history.state };
    if (neutral) delete base.locationsToolbar;
    else base.locationsToolbar = state;

    if (!neutral && !toolbarEntryPushed) {
      // The entry left behind keeps the state it had - which is the unfiltered
      // list, since that is where a first filter is applied from.
      window.history.pushState(base, "");
      toolbarEntryPushed = true;
      return;
    }
    // Once this entry is the toolbar entry it stays the toolbar entry, even
    // after everything is cleared again: clearing is not a place to go back to.
    window.history.replaceState(base, "");
  }

  function applyFilters() {
    saveToolbarState();
    const visible = sorted(allLocations.filter((location) => matches(location)));

    // Only while a radius is active, and only if there is actually something
    // it could not measure - otherwise it is a warning about nothing. Counted
    // against the other filters, so it never mentions a location they hide.
    const unplaced = distanceActive()
      ? allLocations.filter((location) => !hasCoordinates(location) && matches(location, { ignoreDistance: true })).length
      : 0;
    distanceNote.textContent = unplaced ? t("locations.distance.unplaced", { count: unplaced }, unplaced) : "";
    distanceNote.hidden = !unplaced;

    // Two distinct empty states: an untouched trip with no locations at
    // all reads differently from a search that matched none of them.
    emptyState.hidden = allLocations.length > 0;
    noMatchesState.hidden = allLocations.length === 0 || visible.length > 0;

    list.innerHTML = "";
    for (const location of visible) {
      const card = document.createElement("location-card");
      card.setAttribute("location-id", location.id);
      card.setAttribute("title", location.title);
      card.setAttribute("category", location.category);
      if (location.tags?.length) card.setAttribute("tags", JSON.stringify(location.tags));
      if (location.dates?.length) card.setAttribute("dates", JSON.stringify(location.dates));
      if (location.image_url) card.setAttribute("image-url", location.image_url);
      // A real <a href> around the card, for the reason itinerary-tab.js gives
      // for its entry links: middle-click, open-in-new-tab and "copy link
      // address" need an href. Outside the shadow root on purpose: a click in
      // the card retargets to the <location-card> host, and the router's
      // closest("[data-link]") finds this wrapper from there.
      const link = document.createElement("a");
      link.className = "card-link";
      link.href = `/trips/${tripId}/locations/${location.id}`;
      link.setAttribute("data-link", "");
      link.appendChild(card);
      list.appendChild(link);
    }
  }

  // The distance filter needs somewhere to say what went wrong, since it is the
  // one filter that can fail: it needs the device's position, and asking for
  // that can be refused or simply never answered.
  const distanceStatus = container.querySelector(".locations-distance-status");
  const distanceNote = container.querySelector(".locations-distance-note");

  function setStatus(key) {
    distanceStatus.textContent = key ? t(key) : "";
    distanceStatus.hidden = !key;
  }

  // One trigger, two groups. Distance is omitted entirely - rather than
  // rendered and disabled - where the device's position cannot be had at all:
  // no geolocation API, or a page served over plain HTTP, where the browser
  // silently never answers (see geolocation.js). A filter that can only ever
  // fail is worse than no filter. That used to be "render the whole menu or
  // not"; now it is one absent row.
  // Held by name so the tag group can be refilled once the locations arrive:
  // its options *are* the trip's tags, and the menu is built before the fetch.
  const tagGroup = {
    key: "tags",
    // Absent until the trip actually has tags. A filter offering only "Any
    // tag" is a row that can do nothing, and this is the one group whose
    // options can legitimately be empty.
    available: false,
    name: t("locations.filter.tags"),
    neutralLabel: t("locations.filter.anyTag"),
    neutralValue: ANY_TAG,
    activeValue: activeTag,
    items: [],
    onSelect: (value) => {
      activeTag = value;
      applyFilters();
    },
  };

  const dateGroup = {
    key: "date",
    name: t("locations.filter.date"),
    neutralLabel: t("locations.filter.anyDate"),
    neutralValue: ANY_DATE,
    // Never read for this group: isNeutral and currentLabel below both go
    // straight to dateFilter, which is where the restored range lives too.
    activeValue: ANY_DATE,
    isNeutral: () => dateFilter.mode === ANY_DATE,
    currentLabel: () => {
      if (dateFilter.mode === UNSCHEDULED) return t("locations.filter.unscheduled");
      if (dateFilter.mode === SCHEDULED) return t("locations.filter.scheduled");
      if (dateFilter.mode === "range") {
        return formatDateRange(dateFilter.from || null, dateFilter.to || null) ?? t("locations.filter.anyDate");
      }
      return t("locations.filter.anyDate");
    },
    onClear: () => {
      dateFilter = { mode: ANY_DATE, from: "", to: "" };
      applyFilters();
    },
    // Not a list of options: three presets and a range, which no set of
    // menuitemradio rows can express. See renderDatePanel.
    renderPanel: (body, { done }) => renderDatePanel(body, done),
  };

  const filterMenu = renderFilterMenu(container.querySelector(".locations-filter-slot"), {
    ariaLabel: "locations.filter.label",
    title: "locations.filter.title",
    groups: [
      {
        key: "category",
        name: t("locations.filter.category"),
        // "All categories", not the bare "All" the option inside the panel
        // uses. On its own button the funnel icon and the aria-label said what
        // was being filtered; as one row among several it has to say so itself.
        neutralLabel: t("locations.filter.allCategories"),
        neutralValue: "all",
        activeValue: activeFilter,
        items: [
          { value: "all", label: t("locations.filter.all") },
          ...CATEGORIES.map((c) => ({ value: c, label: t(`location.category.${c}`) })),
        ],
        onSelect: (value) => {
          activeFilter = value;
          applyFilters();
        },
      },
      {
        key: "distance",
        available: canLocate(),
        name: t("locations.filter.distance"),
        neutralLabel: t("locations.distance.any"),
        neutralValue: ANY_DISTANCE,
        activeValue: ANY_DISTANCE,
        items: [
          { value: ANY_DISTANCE, label: t("locations.distance.any") },
          ...DISTANCE_RADII_KM.map((km) => ({ value: String(km), label: t("locations.distance.within", { km }) })),
        ],
        onSelect: async (value) => {
          if (value === ANY_DISTANCE) {
            radiusKm = null;
            setStatus(null);
            applyFilters();
            return;
          }
          radiusKm = Number(value);
          setStatus("map.locate.searching");
          try {
            // Asked every time, with the module's own cache deciding whether
            // that costs anything. The old code kept the very first fix for
            // the lifetime of the page, which is the wrong answer to "what is
            // within 5km of me" asked a second time from somewhere else -- and
            // a page left open all day never re-asked at all.
            //
            // Half a kilometre is already far better than a filter that works
            // in kilometres needs, and twelve seconds is the longest it is
            // worth making somebody wait to be told what is nearby. Holding
            // out thirty seconds for a GNSS lock to answer "within 5 km" would
            // be absurd, which is why this does not take the locate control's
            // settings.
            devicePosition = await getCurrentPosition({
              desiredAccuracyM: 500,
              deadlineMs: 12000,
              maxAgeMs: 30000,
            });
          } catch (err) {
            if (signal?.aborted) return;
            // The filter cannot be honored, so it must not look active: the
            // row goes back to "any distance" rather than showing a radius
            // that is not being applied.
            radiusKm = null;
            filterMenu.setActive("distance", ANY_DISTANCE);
            setStatus(locateErrorKey(err.reason || "unavailable"));
            applyFilters();
            return;
          }
          // A fix can take up to twelve seconds, and applyFilters writes the
          // toolbar into the *current* history entry -- which by then may
          // belong to another page or tab (see router.js on the signal).
          if (signal?.aborted) return;
          setStatus(null);
          applyFilters();
        },
      },
      tagGroup,
      dateGroup,
    ],
  });

  // The date panel: three presets, then a range. The presets are rendered as
  // menuitemradio rows so they read like every other option in this menu; the
  // range is a small form, because two dates and an Apply cannot be a row.
  function renderDatePanel(body, done) {
    const presets = [
      { value: ANY_DATE, label: t("locations.filter.anyDate") },
      { value: UNSCHEDULED, label: t("locations.filter.unscheduled") },
      { value: SCHEDULED, label: t("locations.filter.scheduled") },
    ];
    for (const preset of presets) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.setAttribute("role", "menuitemradio");
      btn.setAttribute("aria-checked", String(dateFilter.mode === preset.value));
      btn.dataset.value = preset.value;
      btn.insertAdjacentHTML("afterbegin", icon("check", { className: "menu__check" }));
      const label = document.createElement("span");
      label.textContent = preset.label;
      btn.append(label);
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        dateFilter = { mode: preset.value, from: "", to: "" };
        applyFilters();
        done();
      });
      body.appendChild(btn);
    }

    const form = document.createElement("form");
    form.className = "date-filter";
    // Stacked and labelled rather than two boxes with a dash between them. Two
    // native date inputs side by side are wider than this panel can be at
    // 324px - the panel is anchored to the trigger's right edge, so it had
    // nowhere to grow but off the left of the screen - and once they are
    // stacked, "which one is the start" needs saying out loud anyway.
    form.innerHTML = `
      <label class="date-filter__field">
        <span data-i18n="locations.filter.from"></span>
        <input type="date" name="from" />
      </label>
      <label class="date-filter__field">
        <span data-i18n="locations.filter.to"></span>
        <input type="date" name="to" />
      </label>
      <button type="submit" class="btn btn-secondary" data-i18n="locations.filter.apply"></button>
    `;
    translatePage(form);
    if (dateFilter.mode === "range") {
      form.from.value = dateFilter.from;
      form.to.value = dateFilter.to;
    }
    // The same guard the editor's date card uses: an end before its start is
    // refused by the browser rather than by a message of ours.
    form.from.addEventListener("change", () => {
      form.to.min = form.from.value;
    });
    form.to.min = form.from.value;

    form.addEventListener("submit", (e) => {
      e.preventDefault();
      e.stopPropagation();
      const from = form.from.value;
      const to = form.to.value;
      // Both ends empty is not a range, it is the neutral state - so Apply on
      // an empty form clears the filter rather than pretending to set one.
      dateFilter = from || to ? { mode: "range", from, to } : { mode: ANY_DATE, from: "", to: "" };
      applyFilters();
      done();
    });
    // Enter in either date field submits the form, not the page.
    form.addEventListener("keydown", (e) => e.stopPropagation());
    body.appendChild(form);
  }

  renderMenu(container.querySelector(".locations-sort-slot"), {
    iconName: "arrow-down-up",
    ariaLabel: "locations.sort.label",
    activeValue: sort,
    activeReversed: reversed,
    // Sorting by anything other than the default tints the trigger, so a
    // collapsed icon-only button on a phone still says the order is not the
    // one the list normally has - the same cue the filter funnel carries. A
    // reversed default tints too; menu.js counts that as non-neutral.
    neutralValue: DEFAULT_SORT,
    items: SORTS.map((value) => ({
      value,
      reversible: true,
      label: t(`locations.sort.${value}.asc`),
      reversedLabel: t(`locations.sort.${value}.desc`),
    })),
    onSelect: (value, { reversed: r }) => {
      sort = value;
      reversed = r;
      applyFilters();
    },
  });

  const searchInput = container.querySelector('input[name="q"]');
  searchInput.value = query;
  searchInput.addEventListener("input", (e) => {
    query = e.target.value.trim().toLowerCase();
    applyFilters();
  });

  // Adding a location: one button, or a menu when there are two ways to do it.
  //
  // The assistant's "Suggest locations" flow needs a way in, and the toolbar
  // has no room for a fifth control -- it is a deliberately non-wrapping row
  // that already fits 324px exactly (see the note at the top of this file).
  // So the second way lives behind the button people already press to add
  // something, which costs the primary action one tap and the layout nothing.
  //
  // But only when there *is* a second way. On an instance with no assistant --
  // which is the default, since the assistant needs a model endpoint somebody
  // pays for -- a menu offering one option is a tap in front of the thing you
  // asked for, and it is what this page had before Stage 27 anyway. So that
  // case renders the plain button it always rendered.
  if (editable) {
    const newSlot = container.querySelector(".locations-new-slot");
    const goToNew = () => navigate(`/trips/${tripId}/locations/new`);

    if (hasCapability("assist")) {
      // Both rows are `action: true`: neither is a state the menu is now in,
      // so menuitemradio and aria-checked would both be lies. `label` pins the
      // trigger to "New location" rather than letting it track a selection
      // there is none of.
      renderMenu(newSlot, {
        iconName: "plus",
        label: t("locations.new"),
        ariaLabel: "locations.new",
        triggerClass: "btn btn-primary btn-collapse",
        items: [
          { value: "blank", label: t("locations.newBlank"), hint: t("locations.newBlankHint"), iconName: "plus", action: true },
          { value: "suggest", label: t("locations.newSuggest"), hint: t("locations.newSuggestHint"), iconName: "sparkles", action: true },
        ],
        onSelect: (value) => {
          if (value === "suggest") navigate(`/trips/${tripId}/suggest`);
          else goToNew();
        },
      });
    } else {
      newSlot.innerHTML = `<button class="btn btn-primary btn-collapse" data-action="new-location">${icon("plus")} <span data-i18n="locations.new"></span></button>`;
      translatePage(newSlot);
      newSlot.querySelector('[data-action="new-location"]').addEventListener("click", goToNew);
    }
  }

  renderLoading(list);
  allLocations = await api.get(`/trips/${tripId}/locations`);

  // The tag options only exist once the locations do.
  const tags = tripTags();
  tagGroup.available = tags.length > 0;
  tagGroup.items = [
    { value: ANY_TAG, label: t("locations.filter.anyTag") },
    ...tags.map((tag) => ({ value: tag, label: tag })),
  ];
  filterMenu.refresh();

  applyFilters();
  // Everything above is the restored (or default) state being put on screen.
  // From here on a change is somebody changing it, which is what earns an
  // entry.
  toolbarSaveArmed = true;
}
